import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Plus, Search, Home, Pencil, Trash2, MoreHorizontal, Users, Heart, ChevronRight, ArrowLeft, LayoutGrid, List, Download, ArrowUpDown, ArrowUp, ArrowDown, GripVertical, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TableFilterBar from "@/components/table/TableFilterBar";
import TableSettingsGear, { TableColumnsPanel } from "@/components/table/TableSettingsGear";
import { toast } from "sonner";
import FamilyFormDialog from "@/components/FamilyFormDialog";
import { useFreemium } from "@/hooks/useFreemium";
import { cn } from "@/lib/utils";
import type { Tables } from "@/integrations/supabase/types";

type Family = Tables<"families">;
type Member = Tables<"members">;

interface FamilyWithMembers extends Family {
  members: Member[];
}

type ColumnKey = "family_name" | "uid" | "address" | "city" | "state" | "zip_code" | "phone" | "members_count" | "notes";

interface ColumnDef {
  key: ColumnKey;
  label: string;
  sortField: string;
  render: (f: FamilyWithMembers) => React.ReactNode;
}

type SortDir = "asc" | "desc" | null;

interface FamiliesProps {
  hideTitle?: boolean;
  hideActions?: boolean;
  onAdd?: () => void;
}

const FAMILIES_PAGE_SIZE_STORAGE_KEY = "families_table_page_size";

const Families = ({ hideTitle = false, hideActions = false, onAdd }: FamiliesProps) => {
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editFamily, setEditFamily] = useState<FamilyWithMembers | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [viewFamily, setViewFamily] = useState<FamilyWithMembers | null>(null);
  const [viewMode, setViewMode] = useState<"cards" | "table">("table");
  const [pageSize, setPageSize] = useState(() => {
    if (typeof window === "undefined") return 10;
    const raw = window.localStorage.getItem(FAMILIES_PAGE_SIZE_STORAGE_KEY);
    const parsed = raw ? Number(raw) : NaN;
    return [10, 20, 50, 100].includes(parsed) ? parsed : 10;
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string>("family_name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(
    new Set(["family_name", "uid", "address", "city", "state", "zip_code", "phone", "members_count"])
  );
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [summaryEditMode, setSummaryEditMode] = useState(false);
  const [summaryDraggedId, setSummaryDraggedId] = useState<string | null>(null);
  const [summaryDragHiddenId, setSummaryDragHiddenId] = useState<string | null>(null);
  const [summaryLastDragOverId, setSummaryLastDragOverId] = useState<string | null>(null);
  const summaryStorageKey = "families-summary-layout:v1";
  const [cityFilters, setCityFilters] = useState<Set<string>>(new Set());
  const [stateFilters, setStateFilters] = useState<Set<string>>(new Set());
  const toggleCityFilter = (val: string) => { setCityFilters((prev) => { const next = new Set(prev); if (next.has(val)) next.delete(val); else next.add(val); return next; }); };
  const toggleStateFilter = (val: string) => { setStateFilters((prev) => { const next = new Set(prev); if (next.has(val)) next.delete(val); else next.add(val); return next; }); };
  const queryClient = useQueryClient();
  const { requireSubscription, isPreview } = useFreemium();

  const { data: families = [], isLoading } = useQuery({
    queryKey: ["families"],
    queryFn: async () => {
      const { data: fams, error: fErr } = await supabase
        .from("families")
        .select("*")
        .order("family_name");
      if (fErr) throw fErr;

      const { data: links, error: lErr } = await supabase
        .from("family_members")
        .select("family_id, member_id, members(*)");
      if (lErr) throw lErr;

      const memberMap: Record<string, Member[]> = {};
      (links as any[])?.forEach((link) => {
        if (!memberMap[link.family_id]) memberMap[link.family_id] = [];
        if (link.members) memberMap[link.family_id].push(link.members);
      });

      return (fams as Family[]).map((f) => ({
        ...f,
        members: memberMap[f.id] || [],
      })) as FamilyWithMembers[];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("families").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["families"] });
      toast.success("Family deleted");
      setDeleteId(null);
      if (viewFamily && viewFamily.id === deleteId) setViewFamily(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const uniqueCities = useMemo(() => [...new Set(families.map((f) => (f as any).city || "—"))].filter(Boolean).sort(), [families]);
  const uniqueStates = useMemo(() => [...new Set(families.map((f) => (f as any).state || "—"))].filter(Boolean).sort(), [families]);

  const averageFamilySize = useMemo(
    () => (families.length > 0 ? families.reduce((sum, family) => sum + family.members.length, 0) / families.length : 0),
    [families]
  );

  const maritalStats = useMemo(() => {
    const allMembers = families.flatMap((f) => f.members);
    return {
      single: allMembers.filter((m) => (m as any).marital_status === "Single").length,
      married: allMembers.filter((m) => (m as any).marital_status === "Married").length,
      widowed: allMembers.filter((m) => (m as any).marital_status === "Widowed").length,
      divorced: allMembers.filter((m) => (m as any).marital_status === "Divorced").length,
      separated: allMembers.filter((m) => (m as any).marital_status === "Separated").length,
    };
  }, [families]);

  const summaryDefs = useMemo(
    () => [
      { id: "total_families", label: "Total Families", value: String(families.length), icon: Home, rgb: "9,102,241" },
      { id: "avg_family_size", label: "Avg Family Size", value: averageFamilySize.toFixed(1), icon: Users, rgb: "9,102,241" },
      { id: "single", label: "Single", value: String(maritalStats.single), icon: Users, rgb: "9,102,241" },
      { id: "married", label: "Married", value: String(maritalStats.married), icon: Heart, rgb: "9,102,241" },
      { id: "widowed", label: "Widowed", value: String(maritalStats.widowed), icon: Users, rgb: "9,102,241" },
      { id: "divorced", label: "Divorced", value: String(maritalStats.divorced), icon: Users, rgb: "9,102,241" },
      { id: "separated", label: "Separated", value: String(maritalStats.separated), icon: Users, rgb: "9,102,241" },
    ],
    [families.length, averageFamilySize, maritalStats]
  );

  const summaryById = useMemo(
    () => new Map(summaryDefs.map((card) => [card.id, card] as const)),
    [summaryDefs]
  );
  const allSummaryIds = useMemo(() => summaryDefs.map((card) => card.id), [summaryDefs]);
  const [visibleSummaryIds, setVisibleSummaryIds] = useState<string[]>([]);
  const [hiddenSummaryIds, setHiddenSummaryIds] = useState<string[]>([]);

  useEffect(() => {
    const raw = localStorage.getItem(summaryStorageKey);
    if (!raw) {
      setVisibleSummaryIds(allSummaryIds);
      setHiddenSummaryIds([]);
      return;
    }
    try {
      const parsed = JSON.parse(raw) as { visibleSummaryIds?: string[]; hiddenSummaryIds?: string[] };
      const visible = (parsed.visibleSummaryIds || []).filter((id) => allSummaryIds.includes(id));
      const hidden = (parsed.hiddenSummaryIds || []).filter((id) => allSummaryIds.includes(id) && !visible.includes(id));
      const missing = allSummaryIds.filter((id) => !visible.includes(id) && !hidden.includes(id));
      setVisibleSummaryIds(visible.length > 0 ? visible : allSummaryIds);
      setHiddenSummaryIds([...hidden, ...missing]);
    } catch {
      setVisibleSummaryIds(allSummaryIds);
      setHiddenSummaryIds([]);
    }
  }, [allSummaryIds]);

  useEffect(() => {
    if (visibleSummaryIds.length === 0 && allSummaryIds.length > 0) return;
    localStorage.setItem(summaryStorageKey, JSON.stringify({ visibleSummaryIds, hiddenSummaryIds }));
  }, [visibleSummaryIds, hiddenSummaryIds, allSummaryIds.length]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(FAMILIES_PAGE_SIZE_STORAGE_KEY, String(pageSize));
  }, [pageSize]);

  const visibleSummaryCards = useMemo(
    () => visibleSummaryIds.map((id) => summaryById.get(id)).filter(Boolean),
    [visibleSummaryIds, summaryById]
  );

  const hiddenSummaryCards = useMemo(
    () => hiddenSummaryIds.map((id) => summaryById.get(id)).filter(Boolean),
    [hiddenSummaryIds, summaryById]
  );

  const moveSummaryCard = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    setVisibleSummaryIds((prev) => {
      const from = prev.indexOf(fromId);
      const to = prev.indexOf(toId);
      if (from === -1 || to === -1) return prev;
      const next = [...prev];
      next.splice(from, 1);
      next.splice(to, 0, fromId);
      return next;
    });
  };

  const removeSummaryCard = (id: string) => {
    setVisibleSummaryIds((prev) => prev.filter((cardId) => cardId !== id));
    setHiddenSummaryIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const addSummaryCard = (id: string) => {
    setHiddenSummaryIds((prev) => prev.filter((cardId) => cardId !== id));
    setVisibleSummaryIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const filtered = useMemo(() => {
    let result = families.filter((f) => {
      const matchesSearch = f.family_name.toLowerCase().includes(search.toLowerCase()) ||
        (f.address?.toLowerCase().includes(search.toLowerCase()) ?? false) ||
        ((f as any).city?.toLowerCase().includes(search.toLowerCase()) ?? false);
      const matchesCity = cityFilters.size === 0 || cityFilters.has((f as any).city || "—");
      const matchesState = stateFilters.size === 0 || stateFilters.has((f as any).state || "—");
      return matchesSearch && matchesCity && matchesState;
    });

    if (sortColumn && sortDir) {
      result = [...result].sort((a, b) => {
        let aVal: any, bVal: any;
        if (sortColumn === "members_count") {
          aVal = a.members.length;
          bVal = b.members.length;
          const cmp = aVal - bVal;
          return sortDir === "asc" ? cmp : -cmp;
        }
        aVal = (a as any)[sortColumn] ?? "";
        bVal = (b as any)[sortColumn] ?? "";
        const cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: "base" });
        return sortDir === "asc" ? cmp : -cmp;
      });
    }

    return result;
  }, [families, search, cityFilters, stateFilters, sortColumn, sortDir]);

  const totalFamilyPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const adjFamilyPage = Math.min(currentPage, totalFamilyPages);
  const pagedFamilies = filtered.slice((adjFamilyPage - 1) * pageSize, adjFamilyPage * pageSize);
  const isFamilySearchEmpty = pagedFamilies.length === 0 && families.length !== 0;
  const familyEmptyRowCount = families.length === 0
    ? 0
    : Math.max(0, pageSize - pagedFamilies.length - (isFamilySearchEmpty ? 2 : 0));
  const familyDisplayRangeStart = filtered.length === 0 ? 0 : (adjFamilyPage - 1) * pageSize + 1;
  const familyDisplayRangeEnd = adjFamilyPage * pageSize;

  const toggleSort = (field: string) => {
    if (sortColumn === field) {
      if (sortDir === "asc") setSortDir("desc");
      else if (sortDir === "desc") { setSortColumn(""); setSortDir(null); }
    } else {
      setSortColumn(field);
      setSortDir("asc");
    }
  };

  const toggleColumn = (key: ColumnKey) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); }
      else next.add(key);
      return next;
    });
  };

  const SortIcon = ({ field, active }: { field: string; active: boolean }) => {
    if (!active) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-0 transition-opacity group-hover:opacity-70" />;
    }
    return sortDir === "asc"
      ? <ArrowUp className="w-3 h-3 ml-1 opacity-100" />
      : <ArrowDown className="w-3 h-3 ml-1 opacity-100" />;
  };

  const columns: ColumnDef[] = [
    {
      key: "family_name", label: "Family Name", sortField: "family_name",
      render: (f) => (
        <button onClick={() => setViewFamily(f)} className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:underline text-left min-w-0">
          <Home className="w-4 h-4 text-primary flex-shrink-0" />
          <span className="truncate">{f.family_name}</span>
        </button>
      ),
    },
    {
      key: "uid", label: "UID", sortField: "id",
      render: (f) => <span className="text-xs font-mono text-muted-foreground">F-{f.id.slice(0, 6).toUpperCase()}</span>,
    },
    { key: "address", label: "Address", sortField: "address", render: (f) => <span className="text-sm text-muted-foreground">{f.address || "—"}</span> },
    { key: "city", label: "City", sortField: "city", render: (f) => <span className="text-sm text-muted-foreground">{(f as any).city || "—"}</span> },
    { key: "state", label: "State", sortField: "state", render: (f) => <span className="text-sm text-muted-foreground">{(f as any).state || "—"}</span> },
    { key: "zip_code", label: "ZIP", sortField: "zip_code", render: (f) => <span className="text-sm text-muted-foreground">{(f as any).zip_code || "—"}</span> },
    { key: "phone", label: "Phone", sortField: "phone", render: (f) => <span className="text-sm text-muted-foreground">{f.phone || "—"}</span> },
    {
      key: "members_count", label: "Members", sortField: "members_count",
      render: (f) => <Badge variant="secondary">{f.members.length}</Badge>,
    },
    { key: "notes", label: "Notes", sortField: "notes", render: (f) => <span className="text-sm text-muted-foreground truncate max-w-[200px] block">{f.notes || "—"}</span> },
  ];

  const activeColumns = columns.filter((c) => visibleColumns.has(c.key));

  const exportCSV = () => {
    const headers = ["Family Name", "Address", "City", "State", "ZIP Code", "Phone", "Members", "Notes"];
    const rows = families.map((f) => [
      f.family_name, f.address || "", (f as any).city || "", (f as any).state || "", (f as any).zip_code || "", f.phone || "", f.members.length, f.notes || "",
    ]);
    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "families.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Families exported!");
  };

  const formatLocation = (fam: FamilyWithMembers) => {
    const parts = [(fam as any).city, (fam as any).state, (fam as any).zip_code].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : null;
  };

  // Family profile view
  if (viewFamily) {
    const fam = families.find((f) => f.id === viewFamily.id) || viewFamily;
    const location = formatLocation(fam);
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
            <Button variant="outline" size="sm" onClick={() => setViewFamily(null)}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">{fam.family_name}</h1>
              <p className="text-xs font-mono text-muted-foreground mt-0.5">F-{fam.id.slice(0, 6).toUpperCase()}</p>
              <p className="text-muted-foreground mt-0.5">{fam.address || "No address"}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="self-start sm:self-auto" onClick={() => { setViewFamily(null); setEditFamily(fam); }}>
            <Pencil className="w-4 h-4 mr-2" /> Edit Family
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="stat-card">
            <h3 className="font-heading font-semibold text-foreground mb-4">Family Details</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Phone</span>
                <span className="text-foreground font-medium">{fam.phone || "—"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Address</span>
                <span className="text-foreground font-medium">{fam.address || "—"}</span>
              </div>
              {location && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">City / State / ZIP</span>
                  <span className="text-foreground font-medium">{location}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Members</span>
                <span className="text-foreground font-medium">{fam.members.length}</span>
              </div>
              {fam.notes && (
                <div className="pt-2 border-t border-border/50">
                  <span className="text-muted-foreground">Notes:</span>
                  <p className="text-foreground mt-1">{fam.notes}</p>
                </div>
              )}
            </div>
          </div>

          <div className="stat-card">
            <h3 className="font-heading font-semibold text-foreground mb-4">
              Family Members ({fam.members.length})
            </h3>
            {fam.members.length === 0 ? (
              <p className="text-sm text-muted-foreground">No members assigned yet.</p>
            ) : (
              <div className="space-y-3">
                {fam.members.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 py-2 border-b border-border/50 last:border-0">
                    <Avatar className="w-9 h-9">
                      <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                        {m.full_name.split(" ").map((n) => n[0]).join("")}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium text-foreground">{m.full_name}</p>
                      <p className="text-xs text-muted-foreground">{m.department || "No department"}</p>
                    </div>
                    <Badge variant="secondary" className="ml-auto">
                      {m.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {(!hideTitle || !hideActions) && (
        <div className={cn("flex items-center gap-3", hideTitle ? "justify-end" : "justify-between")}>
          {!hideTitle && (
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Families</h1>
              <p className="text-muted-foreground text-sm mt-1">{families.length} households</p>
            </div>
          )}
          {!hideActions && (
            <div className="flex items-center gap-2 sm:gap-3">
              <Button variant="outline" size="sm" onClick={exportCSV} className="hidden sm:flex">
                <Download className="w-4 h-4 mr-2" /> Export
              </Button>
              <Button variant="outline" size="icon" onClick={exportCSV} className="sm:hidden h-9 w-9">
                <Download className="w-4 h-4" />
              </Button>
              <Button size="sm" onClick={() => setShowAdd(true)}>
                {isPreview ? <span className="mr-1"></span> : null}
                <Plus className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Add Family</span>
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2" onDoubleClick={() => { if (summaryEditMode) setSummaryEditMode(false); }}>
        <div className="grid grid-cols-3 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {visibleSummaryCards.map((card) => {
          if (!card) return null;
          const Icon = card.icon;
          return (
            <Card
              key={card.id}
              className={cn(
                "group relative transition-shadow hover:shadow-md border-slate-100",
                summaryEditMode && "cursor-grab active:cursor-grabbing ring-1 ring-primary/25",
                summaryDragHiddenId === card.id && "opacity-0"
              )}
              draggable={summaryEditMode}
              onDragStart={() => {
                if (!summaryEditMode) return;
                setSummaryDraggedId(card.id);
                setSummaryLastDragOverId(null);
                requestAnimationFrame(() => setSummaryDragHiddenId(card.id));
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (!summaryDraggedId || summaryDraggedId === card.id) return;
                if (summaryLastDragOverId === card.id) return;
                moveSummaryCard(summaryDraggedId, card.id);
                setSummaryLastDragOverId(card.id);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setSummaryLastDragOverId(null);
              }}
              onDragLeave={() => {
                if (summaryLastDragOverId === card.id) setSummaryLastDragOverId(null);
              }}
              onDragEnd={() => {
                setSummaryDraggedId(null);
                setSummaryDragHiddenId(null);
                setSummaryLastDragOverId(null);
              }}
            >
              {summaryEditMode && (
                <button
                  type="button"
                  className="absolute right-7 top-1 z-10 p-1 rounded-md bg-background/90 border border-border transition-colors hover:bg-primary/10"
                  aria-label="Done editing cards"
                  onClick={() => setSummaryEditMode(false)}
                >
                  <Check className="w-3.5 h-3.5 text-primary" />
                </button>
              )}
              <button
                type="button"
                className={cn(
                  "absolute right-1 top-1 z-10 p-1 transition-opacity cursor-grab active:cursor-grabbing",
                  summaryEditMode ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                )}
                aria-label="Drag to rearrange"
                onClick={() => setSummaryEditMode(true)}
              >
                <GripVertical className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
              {summaryEditMode && visibleSummaryIds.length > 1 && (
                <button
                  type="button"
                  className="absolute left-1 top-1 z-10 p-1 rounded-md bg-background/90 border border-border hover:bg-destructive/10"
                  onClick={() => removeSummaryCard(card.id)}
                  aria-label={`Remove ${card.label}`}
                >
                  <Trash2 className="w-3.5 h-3.5 text-destructive" />
                </button>
              )}
              <CardContent className="pt-3 pb-3 px-3 flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <div
                    className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `rgba(${card.rgb}, 0.12)` }}
                  >
                    <Icon className="w-3.5 h-3.5" style={{ color: `rgb(${card.rgb})` }} />
                  </div>
                  <p className="font-bold text-foreground leading-tight text-base">{card.value}</p>
                </div>
                <p className="text-[11px] text-muted-foreground leading-tight">{card.label}</p>
              </CardContent>
            </Card>
          );
        })}
        {summaryEditMode && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="rounded-lg bg-primary/5 hover:bg-primary/10 transition-colors min-h-[92px] flex flex-col items-center justify-center gap-1"
              >
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                  <Plus className="w-5 h-5 text-primary" />
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
              {hiddenSummaryCards.length === 0 ? (
                <DropdownMenuItem disabled className="text-[11px]">All cards are already shown</DropdownMenuItem>
              ) : (
                hiddenSummaryCards.map((hiddenCard) => (
                  <DropdownMenuItem key={hiddenCard!.id} onClick={() => addSummaryCard(hiddenCard!.id)} className="text-[11px]">
                    {hiddenCard!.label}
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        </div>
      </div>

      <div className="stat-card p-0 overflow-visible border-transparent">
      <div className="px-0 py-3 border-b border-transparent relative sm:px-4">
      <TableFilterBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search families..."
        searchFirstOnMobile
        mobileControlsFirst
        compactDesktopFilters
        showMobileFilterSummary
        filterColumns={2}
        filterGroups={[
          { key: "city", label: "City", options: uniqueCities, selected: cityFilters, onToggle: toggleCityFilter, onClear: () => setCityFilters(new Set()) },
          { key: "state", label: "State", options: uniqueStates, selected: stateFilters, onToggle: toggleStateFilter, onClear: () => setStateFilters(new Set()) },
        ]}
        leadingControls={
          <div className="flex items-center border border-border rounded-md overflow-hidden sm:rounded-lg">
            <button
              onClick={() => setViewMode("cards")}
              className={cn("p-2 transition-colors", viewMode === "cards" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary")}
              title="Card view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={cn("p-2 transition-colors", viewMode === "table" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary")}
              title="Table view"
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        }
        extraControls={
          <div className="flex items-center gap-1.5 sm:gap-2">
            {onAdd && (
              <Button size="sm" className="h-7 w-7 p-0 text-[11px] sm:h-9 sm:w-auto sm:px-3 sm:text-sm" onClick={onAdd}>
                {isPreview ? <span className="mr-1"></span> : null}
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
                <span className="hidden sm:inline">Add Family</span>
              </Button>
            )}
          </div>
        }
      />
      </div>

      {columnsOpen && viewMode === "table" && (
        <TableColumnsPanel
          columns={columns.map(c => ({ key: c.key, label: c.label }))}
          visibleColumns={visibleColumns as Set<string>}
          onToggle={(key) => toggleColumn(key as ColumnKey)}
          onClose={() => setColumnsOpen(false)}
        />
      )}

      <div className="min-h-[420px]">
      {isLoading ? (
        <div className="flex min-h-[420px] items-center justify-center text-muted-foreground">Loading...</div>
      ) : viewMode === "cards" ? (
        filtered.length === 0 ? (
          <div className="flex min-h-[420px] items-center justify-center text-center px-4">
            <p className="text-muted-foreground">
              {families.length === 0 ? "No families yet. Create your first family!" : "No families match your search."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((family) => {
              return (
              <div key={family.id} className="stat-card group">
                <div className="flex items-start justify-between mb-3">
                  <div
                    className="flex items-center gap-3 cursor-pointer"
                    onClick={() => setViewFamily(family)}
                  >
                    <div className="hidden md:flex w-10 h-10 rounded-lg bg-primary/10 items-center justify-center flex-shrink-0">
                      <Home className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-foreground hover:underline">{family.family_name}</h3>
                      <p className="text-xs font-mono text-muted-foreground">F-{family.id.slice(0, 6).toUpperCase()}</p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-1 rounded hover:bg-secondary transition-colors opacity-0 group-hover:opacity-100">
                        <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => { if (!requireSubscription()) return; setEditFamily(family); }}>
                        <Pencil className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { if (!requireSubscription()) return; setDeleteId(family.id); }} className="text-destructive focus:text-destructive">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="hidden md:block space-y-1 text-xs text-muted-foreground mb-3">
                  {family.address && <p className="truncate">{family.address}</p>}
                  {[(family as any).city, (family as any).state, (family as any).zip_code].filter(Boolean).join(", ") && <p className="truncate">{[(family as any).city, (family as any).state, (family as any).zip_code].filter(Boolean).join(", ")}</p>}
                  {family.phone && <p>{family.phone}</p>}
                  {(family as any).notes && <p className="truncate italic">{(family as any).notes}</p>}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-border/50">
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Users className="w-4 h-4" />
                    <span>{family.members.length} member{family.members.length !== 1 ? "s" : ""}</span>
                  </div>
                  <button
                    onClick={() => setViewFamily(family)}
                    className="hidden md:flex text-xs text-primary font-medium items-center gap-1 hover:underline"
                  >
                    View <ChevronRight className="w-3 h-3" />
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        )
      ) : (
        <>
          {/* Mobile card view for table mode */}
          <div className="stat-card p-0 overflow-hidden divide-y divide-border md:hidden">
            {filtered.map((family) => {
            return (
              <div
                key={family.id}
                className="cursor-pointer px-4 py-3 transition-colors hover:bg-secondary/20"
                onClick={() => setViewFamily(family)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <Home className="w-4 h-4 text-primary shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground truncate">{family.family_name}</p>
                      <p className="text-xs font-mono text-muted-foreground">F-{family.id.slice(0, 6).toUpperCase()}</p>
                      <p className="text-xs text-muted-foreground mt-1">{family.members.length} member{family.members.length !== 1 ? "s" : ""}</p>
                      {family.address && <p className="text-xs text-muted-foreground mt-1 truncate">{family.address}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 rounded hover:bg-secondary transition-colors">
                          <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { if (!requireSubscription()) return; setEditFamily(family); }}>
                          <Pencil className="w-4 h-4 mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { if (!requireSubscription()) return; setDeleteId(family.id); }} className="text-destructive focus:text-destructive">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            );
          })}
          </div>

          {/* Desktop table view */}
          <div className="hidden md:block relative w-full -mx-4 rounded-none overflow-hidden sm:mx-0">
            <div className="w-full overflow-x-auto">
              <table className="w-full min-w-full">
                <thead>
                  <tr className="bg-gray-200">
                  {activeColumns.map((col) => (
                    <th
                      key={col.key}
                      className="group text-left text-[10px] sm:text-sm font-semibold text-gray-700 tracking-wider px-4 py-3 cursor-pointer select-none hover:text-gray-900 transition-colors"
                      onClick={() => toggleSort(col.sortField)}
                    >
                      <span className="inline-flex items-center">
                        {col.label}
                        <SortIcon field={col.sortField} active={sortColumn === col.sortField && !!sortDir} />
                      </span>
                    </th>
                  ))}
                    <th className="w-[8%] text-center text-[10px] sm:text-sm font-semibold text-gray-700 tracking-wider px-2 py-2">
                      <TableSettingsGear
                        columns={columns.map(c => ({ key: c.key, label: c.label }))}
                        visibleColumns={visibleColumns as Set<string>}
                        onToggle={(key) => toggleColumn(key as ColumnKey)}
                        open={columnsOpen}
                        onOpenChange={setColumnsOpen}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagedFamilies.length === 0 && families.length === 0 ? (
                    <tr>
                      <td colSpan={activeColumns.length + 1} className="px-4 py-10 text-center text-sm text-muted-foreground bg-background">
                        No families yet. Create your first family!
                      </td>
                    </tr>
                  ) : isFamilySearchEmpty ? (
                    <>
                      <tr className="h-[42px] border-b border-transparent"><td colSpan={activeColumns.length + 1} className="px-4 py-2" /></tr>
                      <tr className="h-[42px] border-b border-transparent">
                        <td colSpan={activeColumns.length + 1} className="px-4 py-2 text-center text-sm text-muted-foreground">
                          No families match your search.
                        </td>
                      </tr>
                    </>
                  ) : (
                    pagedFamilies.map((family, idx) => (
                      <tr key={family.id} className={cn("border-b border-transparent hover:bg-primary/10 transition-colors cursor-pointer", idx % 2 === 0 ? "bg-background" : "bg-muted/20")} onClick={() => setViewFamily(family)}>
                      {activeColumns.map((col) => (
                          <td key={col.key} className="px-4 py-2 max-w-[200px] truncate text-sm">{col.render(family)}</td>
                      ))}
                        <td className="px-2 py-0.5 text-center w-[8%]" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1 rounded hover:bg-secondary transition-colors">
                              <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { if (!requireSubscription()) return; setEditFamily(family); }}>
                              <Pencil className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { if (!requireSubscription()) return; setDeleteId(family.id); }} className="text-destructive focus:text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        </td>
                      </tr>
                    ))
                  )}
                  {Array.from({ length: familyEmptyRowCount }).map((_, idx) => (
                    <tr key={`family-empty-row-${idx}`} className={cn("h-[42px] border-b border-transparent", idx % 2 === 0 ? "bg-background" : "bg-muted/20")}>
                      <td colSpan={activeColumns.length + 1} className="px-4 py-2" />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex flex-col items-center gap-2 px-4 py-3 sm:flex-row sm:justify-between sm:gap-0">
              <p className="hidden text-xs text-muted-foreground sm:block">Showing {familyDisplayRangeStart}–{familyDisplayRangeEnd} of {filtered.length}</p>
              <div className="flex w-full items-center justify-center gap-2 sm:w-auto">
                <span className="hidden text-xs text-muted-foreground sm:block">Rows per page:</span>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                  <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                {totalFamilyPages > 1 && (
                  <>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjFamilyPage === 1} onClick={() => setCurrentPage(1)}>«</Button>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjFamilyPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>‹</Button>
                    <span className="text-xs px-2">{adjFamilyPage} / {totalFamilyPages}</span>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjFamilyPage === totalFamilyPages} onClick={() => setCurrentPage((p) => p + 1)}>›</Button>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" disabled={adjFamilyPage === totalFamilyPages} onClick={() => setCurrentPage(totalFamilyPages)}>»</Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      </div>
      </div>

      <FamilyFormDialog
        open={showAdd || !!editFamily}
        onClose={() => { setShowAdd(false); setEditFamily(null); }}
        family={editFamily}
        assignedMemberIds={editFamily?.members.map((m) => m.id) || []}
      />

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this family?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the family record and unlink all members.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!requireSubscription()) return;
                if (deleteId) deleteMutation.mutate(deleteId);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Families;
